import { UserPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { ClientReturnFolder } from "@redotech/redo-views-common/folders";
import { ClientReturnView } from "@redotech/redo-views-common/views";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";

export function recordViewsQueryKey(teamId: string, type: ReturnTypeEnum) {
  return ["record-views", teamId, type] as const;
}

const NO_VIEWS: ClientReturnView[] = [];
const NO_FOLDERS: ClientReturnFolder[] = [];

/** Every saved view and folder of one record type, shared by the side nav and the tables. */
export function useRecordViews(
  type: ReturnTypeEnum,
  ...permissions: UserPermissions[]
): {
  views: ClientReturnView[];
  folders: ClientReturnFolder[];
  reloadViews: () => Promise<void>;
} {
  const team = useRequiredContext(TeamContext);
  const canView = useHasPermission(...permissions);
  const queryClient = useQueryClient();
  const queryKey = recordViewsQueryKey(team._id, type);

  const query = useRpcQuery(RedoMerchantRpcClientContext, {
    method: "getReturnViewsAndFolders",
    args: { type },
    options: { queryKey, enabled: canView, placeholderData: keepPreviousData },
  });

  const reloadViews = useHandler(async () => {
    await queryClient.invalidateQueries({ queryKey });
  });

  return {
    views: query.data?.views ?? NO_VIEWS,
    folders: query.data?.folders ?? NO_FOLDERS,
    reloadViews,
  };
}
